import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
  LogOut, TrendingDown, FileText, MessageSquare, Upload, Send,
  Loader2, CheckCircle, Clock, AlertCircle, Paperclip, BarChart3,
  Share2, Phone, Download, Copy, Users, Gift, Calendar, Eye, X,
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });

const ClientDashboard = () => {
  const { logout } = useAuth();
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [newMessage, setNewMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState('utility_bill');
  const [previewDoc, setPreviewDoc] = useState(null);
  const [showCallback, setShowCallback] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [dashRes, sumRes, refRes] = await Promise.all([
        ax.get('/api/client/dashboard'),
        ax.get('/api/client/savings-summary').catch(() => ({ data: null })),
        ax.get('/api/client/referral/me').catch(() => ({ data: null })),
      ]);
      setData(dashRes.data);
      setSummary(sumRes.data);
      setReferral(refRes.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    try {
      await ax.post('/api/client/messages', { message: newMessage });
      setNewMessage('');
      fetchAll();
    } catch (e) { console.error(e); }
  };

  const uploadFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('doc_type', uploadType);
      try {
        await ax.post('/api/client/documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      } catch (err) { console.error(err); }
    }
    setUploading(false);
    fetchAll();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    uploadFiles(Array.from(e.dataTransfer.files));
  };

  const statusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return <CheckCircle size={20} style={{ color: '#059669' }} />;
      case 'in review': return <Clock size={20} style={{ color: '#D97706' }} />;
      case 'renewal due': return <AlertCircle size={20} style={{ color: '#DC2626' }} />;
      default: return <Clock size={20} style={{ color: '#6B7280' }} />;
    }
  };

  const downloadStatement = () => {
    if (!summary) return;
    // Build a simple text-based statement
    const lines = [
      'LAST MILE ADVISORS — SAVINGS STATEMENT',
      '='.repeat(50),
      `Customer: ${summary.profile.name || '—'}`,
      `Email: ${summary.profile.email || '—'}`,
      `State: ${summary.profile.state || '—'}`,
      `Energy Type: ${summary.profile.energy_type || '—'}`,
      '',
      `Previous Rate: $${summary.profile.previous_rate || 0}/kWh`,
      `Current Rate:  $${summary.profile.current_rate || 0}/kWh`,
      `Savings:       ${summary.savings_percent || 0}%`,
      '',
      `Monthly Savings: $${summary.monthly_savings || 0}`,
      `Annual Savings:  $${summary.annual_savings || 0}`,
      '',
      'MONTHLY BREAKDOWN',
      '-'.repeat(50),
      ...summary.monthly_breakdown.map(m =>
        `${m.label.padEnd(12)} Old: $${m.old_cost.toString().padEnd(8)} New: $${m.new_cost.toString().padEnd(8)} Saved: $${m.saved}`
      ),
      '',
      `Generated on: ${new Date().toLocaleString()}`,
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `LMA_Savings_Statement_${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <main className="page-content">
        <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 72px)' }}>
          <Loader2 size={32} className="animate-spin" style={{ color: 'var(--teal)' }} />
        </div>
      </main>
    );
  }

  const profile = data?.user || {};
  const contracts = data?.contracts || [];
  const documents = data?.documents || [];
  const messages = data?.messages || [];

  const savingsPct = summary?.savings_percent;
  const daysRemaining = summary?.days_remaining;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <TrendingDown size={16} /> },
    { id: 'savings', label: 'Savings', icon: <BarChart3 size={16} /> },
    { id: 'documents', label: 'Documents', icon: <FileText size={16} />, count: documents.length },
    { id: 'messages', label: 'Messages', icon: <MessageSquare size={16} />, count: messages.length },
    { id: 'refer', label: 'Refer & Earn', icon: <Gift size={16} /> },
  ];

  const referralUrl = referral ? `${window.location.origin}/contact?ref=${referral.code}` : '';

  return (
    <main className="page-content" data-testid="client-dashboard">
      {/* Header */}
      <div style={{ background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-default)', padding: '1rem 0' }}>
        <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.375rem', fontWeight: 700, color: 'var(--navy)', margin: 0 }}>
              My Account
            </h1>
            <p style={{ fontSize: '0.813rem', color: 'var(--text-muted)', margin: 0 }}>
              Welcome, {profile.name || profile.email}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button onClick={() => setShowCallback(true)} className="btn-secondary" data-testid="request-callback-btn" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Phone size={16} /> Request Callback
            </Button>
            <Button onClick={logout} className="btn-secondary" data-testid="client-logout-btn" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <LogOut size={16} /> Sign Out
            </Button>
          </div>
        </div>
      </div>

      <div className="container" style={{ padding: '1.5rem var(--container-padding)' }}>
        {/* Renewal alert banner */}
        {daysRemaining != null && daysRemaining <= 90 && daysRemaining >= 0 && (
          <div style={{
            background: daysRemaining <= 30 ? '#FEE2E2' : '#FEF3C7',
            border: `1px solid ${daysRemaining <= 30 ? '#FCA5A5' : '#FDE68A'}`,
            padding: '0.875rem 1rem', borderRadius: 8, marginBottom: '1.25rem',
            display: 'flex', alignItems: 'center', gap: 12,
          }} data-testid="renewal-alert">
            <AlertCircle size={20} style={{ color: daysRemaining <= 30 ? '#DC2626' : '#D97706' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, color: daysRemaining <= 30 ? '#991B1B' : '#92400E' }}>
                Contract renewal in {daysRemaining} days
              </div>
              <div style={{ fontSize: '0.813rem', color: '#6B7280' }}>
                Contact your advisor to lock in the best renewal rate.
              </div>
            </div>
            <Button className="btn-primary" onClick={() => setShowCallback(true)} data-testid="renewal-callback-btn">
              Request Callback
            </Button>
          </div>
        )}

        {/* Status Card */}
        <Card style={{ background: 'var(--bg-base)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', marginBottom: '1.5rem', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
          <CardContent style={{ padding: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
              {statusIcon(profile.contract_status)}
              <div>
                <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, color: 'var(--navy)', fontSize: '1rem' }}>
                  Account Status: {profile.contract_status || 'Pending Review'}
                </div>
                <div style={{ fontSize: '0.813rem', color: 'var(--text-muted)' }}>
                  {profile.energy_type ? `${profile.energy_type} • ${profile.state || ''}` : 'Contact your advisor to set up your account'}
                </div>
              </div>
            </div>

            {savingsPct != null && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', padding: '1rem', background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-default)' }}>
                <StatCell label="Previous Rate" value={`$${profile.previous_rate}/kWh`} color="#DC2626" />
                <StatCell label="Current Rate" value={`$${profile.current_rate}/kWh`} color="#059669" />
                <StatCell label="Savings" value={`${savingsPct}%`} color="var(--teal)" />
                {summary?.annual_savings > 0 && <StatCell label="Annual Savings" value={`$${summary.annual_savings.toLocaleString()}`} color="var(--teal)" />}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', borderBottom: '1px solid var(--border-default)', paddingBottom: '0.5rem', flexWrap: 'wrap' }}>
          {tabs.map(t => (
            <button
              key={t.id} onClick={() => setTab(t.id)}
              data-testid={`client-tab-${t.id}`}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem',
                border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600,
                fontFamily: 'var(--font-heading)',
                background: tab === t.id ? 'var(--teal)' : 'transparent',
                color: tab === t.id ? '#fff' : 'var(--text-secondary)',
              }}
            >
              {t.icon} {t.label}
              {t.count !== undefined && (
                <span style={{
                  background: tab === t.id ? 'rgba(255,255,255,0.2)' : 'var(--bg-surface-highlight)',
                  padding: '0.125rem 0.5rem', borderRadius: '100px', fontSize: '0.75rem'
                }}>{t.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* OVERVIEW TAB */}
        {tab === 'overview' && (
          <div>
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1rem', fontWeight: 600, color: 'var(--navy)', marginBottom: '0.75rem' }}>Contract History</h3>
            {contracts.length === 0 ? (
              <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', padding: '1.5rem', textAlign: 'center', background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-default)' }}>
                No contracts yet. Your advisor will update your account once your contract is processed.
              </p>
            ) : (
              <div style={{ overflowX: 'auto', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)' }}>
                <table className="state-table">
                  <thead>
                    <tr>
                      <th>Date</th><th>Energy Type</th><th>State</th><th>Status</th><th>Savings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contracts.map((c, i) => (
                      <tr key={i}>
                        <td style={{ fontSize: '0.813rem' }}>{c.timestamp ? new Date(c.timestamp).toLocaleDateString() : '—'}</td>
                        <td>{c.energy_type || '—'}</td>
                        <td>{c.state || '—'}</td>
                        <td>
                          <span className={`status-badge ${c.status === 'Active' ? 'status-yes' : c.status === 'New' ? 'status-partial' : 'status-no'}`}>
                            {c.status || 'Pending'}
                          </span>
                        </td>
                        <td style={{ fontWeight: 600, color: 'var(--teal)' }}>{c.savings_percent ? `${c.savings_percent}%` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* SAVINGS TAB */}
        {tab === 'savings' && summary && (
          <div data-testid="savings-tab">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1rem', fontWeight: 600, color: 'var(--navy)', margin: 0 }}>Savings Overview</h3>
              <Button onClick={downloadStatement} className="btn-secondary" data-testid="download-statement-btn">
                <Download size={14} style={{ marginRight: 6 }} /> Download Statement
              </Button>
            </div>

            {/* Cumulative savings line */}
            <Card style={{ marginBottom: '1rem', border: '1px solid var(--border-default)' }}>
              <CardContent style={{ padding: '1rem' }}>
                <div style={{ fontSize: '0.813rem', color: '#6B7280', marginBottom: '0.75rem' }}>Cumulative Savings (12-month projection)</div>
                {summary.monthly_breakdown.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={summary.monthly_breakdown} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${v}`} />
                      <Tooltip formatter={v => `$${v}`} />
                      <Line type="monotone" dataKey="cumulative" stroke="#14B8A6" strokeWidth={2.5} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <div style={{ color: '#9CA3AF', textAlign: 'center', padding: '2rem' }}>Waiting for contract to activate.</div>}
              </CardContent>
            </Card>

            {/* Monthly bar chart old vs new */}
            <Card style={{ border: '1px solid var(--border-default)' }}>
              <CardContent style={{ padding: '1rem' }}>
                <div style={{ fontSize: '0.813rem', color: '#6B7280', marginBottom: '0.75rem' }}>Monthly Cost Comparison</div>
                {summary.monthly_breakdown.length > 0 && summary.monthly_breakdown[0].old_cost > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={summary.monthly_breakdown} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${v}`} />
                      <Tooltip formatter={v => `$${v}`} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="old_cost" fill="#EF4444" name="Old Cost" />
                      <Bar dataKey="new_cost" fill="#14B8A6" name="New Cost" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div style={{ color: '#9CA3AF', textAlign: 'center', padding: '2rem' }}>Once your rates are set, we&apos;ll show your monthly comparison here.</div>}
              </CardContent>
            </Card>
          </div>
        )}

        {/* DOCUMENTS TAB */}
        {tab === 'documents' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: 8 }}>
              <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1rem', fontWeight: 600, color: 'var(--navy)', margin: 0 }}>Your Documents</h3>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <select value={uploadType} onChange={(e) => setUploadType(e.target.value)} style={{ padding: '0.5rem', borderRadius: 6, border: '1px solid #E5E7EB', fontSize: '0.813rem' }} data-testid="doc-type-select">
                  <option value="utility_bill">Utility Bill</option>
                  <option value="contract">Contract</option>
                  <option value="id">ID / Verification</option>
                  <option value="other">Other</option>
                </select>
                <label style={{ cursor: 'pointer' }}>
                  <input type="file" multiple onChange={(e) => uploadFiles(Array.from(e.target.files))} style={{ display: 'none' }} data-testid="upload-document-input" />
                  <Button as="span" className="btn-primary" disabled={uploading} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                    Upload
                  </Button>
                </label>
              </div>
            </div>

            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              style={{
                border: '2px dashed #D1D5DB', borderRadius: 10, padding: '1.25rem',
                textAlign: 'center', background: '#FAFBFC', marginBottom: '1rem', cursor: 'default',
              }}
              data-testid="doc-dropzone"
            >
              <Paperclip size={20} style={{ color: '#9CA3AF' }} />
              <div style={{ fontSize: '0.813rem', color: '#6B7280', marginTop: 4 }}>
                Drop files here to upload as <strong>{uploadType.replace('_', ' ')}</strong>
              </div>
            </div>

            {documents.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-default)' }}>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>No documents yet.</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                {documents.map((doc, i) => (
                  <Card key={i} style={{ background: 'var(--bg-base)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)' }}>
                    <CardContent style={{ padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <FileText size={18} style={{ color: 'var(--teal)' }} />
                        <div>
                          <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--navy)' }}>{doc.filename}</div>
                          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                            {doc.doc_type} • {doc.size ? `${(doc.size / 1024).toFixed(0)} KB` : ''} • {new Date(doc.uploaded_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <button onClick={() => setPreviewDoc(doc)} style={{ padding: '4px 10px', border: '1px solid #E5E7EB', borderRadius: 4, background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.75rem' }} data-testid={`preview-doc-${i}`}>
                        <Eye size={13} /> Info
                      </button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* MESSAGES TAB */}
        {tab === 'messages' && (
          <div>
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1rem', fontWeight: 600, color: 'var(--navy)', marginBottom: '0.75rem' }}>Messages with Your Advisor</h3>
            <Card style={{ background: 'var(--bg-base)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)' }}>
              <CardContent style={{ padding: '1.25rem' }}>
                <div style={{ maxHeight: 400, overflowY: 'auto', marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', minHeight: 200 }}>
                  {messages.length === 0 ? (
                    <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', textAlign: 'center', margin: 'auto' }}>
                      No messages yet. Send a message to your LMA advisor below.
                    </p>
                  ) : messages.map((m, i) => (
                    <div key={i} style={{
                      alignSelf: m.sender_role === 'client' ? 'flex-end' : 'flex-start',
                      maxWidth: '80%', padding: '0.625rem 0.875rem', borderRadius: 'var(--radius-md)',
                      background: m.sender_role === 'client' ? 'var(--teal)' : 'var(--bg-surface)',
                      color: m.sender_role === 'client' ? '#fff' : 'var(--text-secondary)',
                      fontSize: '0.875rem',
                    }}>
                      <div style={{ fontWeight: 600, fontSize: '0.75rem', marginBottom: 2 }}>{m.sender_name}</div>
                      {m.message}
                      <div style={{ fontSize: '0.65rem', marginTop: 4, opacity: 0.6 }}>{new Date(m.timestamp).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Input
                    value={newMessage} onChange={e => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    onKeyDown={e => e.key === 'Enter' && sendMessage()}
                    data-testid="client-message-input"
                    style={{ flex: 1, background: 'var(--bg-surface)', border: '1px solid var(--border-default)', color: 'var(--navy)' }}
                  />
                  <Button onClick={sendMessage} className="btn-primary" data-testid="client-send-msg">
                    <Send size={16} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* REFER TAB */}
        {tab === 'refer' && referral && (
          <div data-testid="refer-tab">
            <Card style={{ background: 'linear-gradient(135deg, #F0FDFA 0%, #ECFDF5 100%)', border: '1px solid #A7F3D0', marginBottom: '1rem' }}>
              <CardContent style={{ padding: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                  <Gift size={28} style={{ color: '#059669' }} />
                  <div>
                    <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.125rem', fontWeight: 700, color: 'var(--navy)', margin: 0 }}>
                      Refer a business, save them money
                    </h3>
                    <p style={{ fontSize: '0.813rem', color: '#065F46', margin: '2px 0 0' }}>
                      Share your link with businesses that could save on energy — you&apos;ll be credited when they sign.
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8, background: '#fff', padding: '0.5rem', borderRadius: 8, border: '1px solid #A7F3D0' }}>
                  <input readOnly value={referralUrl} style={{ flex: 1, border: 'none', outline: 'none', fontSize: '0.875rem', color: 'var(--navy)', padding: '0.25rem 0.5rem' }} data-testid="referral-link" />
                  <Button className="btn-primary" onClick={() => { navigator.clipboard.writeText(referralUrl); alert('Copied!'); }} data-testid="copy-referral-btn">
                    <Copy size={14} style={{ marginRight: 6 }} /> Copy Link
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
              <StatCell label="Referrals Sent" value={referral.sent} color="var(--teal)" />
              <StatCell label="Converted" value={referral.converted} color="#059669" />
            </div>

            {referral.recent.length > 0 && (
              <Card style={{ border: '1px solid var(--border-default)' }}>
                <CardContent style={{ padding: '1rem' }}>
                  <div style={{ fontSize: '0.813rem', color: '#6B7280', marginBottom: '0.5rem' }}>Recent Referrals</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {referral.recent.map((r, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem', background: '#F9FAFB', borderRadius: 6 }}>
                        <div>
                          <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--navy)' }}>{r.referee_name || r.referee_email || 'Anonymous'}</div>
                          <div style={{ fontSize: '0.7rem', color: '#6B7280' }}>{new Date(r.created_at).toLocaleDateString()}</div>
                        </div>
                        <span style={{ padding: '2px 8px', borderRadius: 100, fontSize: '0.7rem', fontWeight: 600, background: r.status === 'converted' ? '#D1FAE5' : '#FEF3C7', color: r.status === 'converted' ? '#065F46' : '#92400E' }}>
                          {r.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Callback modal */}
      {showCallback && <CallbackModal onClose={() => setShowCallback(false)} defaultPhone={profile.phone} />}

      {/* Doc info modal */}
      {previewDoc && <DocInfoModal doc={previewDoc} onClose={() => setPreviewDoc(null)} />}
    </main>
  );
};

const StatCell = ({ label, value, color }) => (
  <div>
    <div style={{ fontSize: '0.7rem', fontWeight: 500, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
    <div style={{ fontFamily: 'var(--font-heading)', fontSize: '1.25rem', fontWeight: 700, color }}>{value}</div>
  </div>
);

const CallbackModal = ({ onClose, defaultPhone }) => {
  const [reason, setReason] = useState('');
  const [preferredTime, setPreferredTime] = useState('');
  const [phone, setPhone] = useState(defaultPhone || '');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await ax.post('/api/client/request-callback', { reason, preferred_time: preferredTime, phone });
      setDone(true);
      setTimeout(onClose, 1500);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to submit');
    }
    setSubmitting(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 440, padding: '1.5rem' }} data-testid="callback-modal">
        {done ? (
          <div style={{ textAlign: 'center' }}>
            <CheckCircle size={48} style={{ color: '#10B981', margin: '0 auto 0.75rem' }} />
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.125rem', fontWeight: 700, color: 'var(--navy)' }}>We&apos;ll call you back soon</h3>
            <p style={{ fontSize: '0.875rem', color: '#6B7280' }}>Your advisor has been notified.</p>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.125rem', fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Request a Callback</h3>
              <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}><X size={20} /></button>
            </div>
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              <label>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Phone</div>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="callback-phone" />
              </label>
              <label>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Preferred Time</div>
                <Input value={preferredTime} onChange={(e) => setPreferredTime(e.target.value)} placeholder="e.g. Tomorrow afternoon" data-testid="callback-time" />
              </label>
              <label>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Reason</div>
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="How can we help?" style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #D1D5DB', borderRadius: 6, resize: 'vertical', fontSize: '0.875rem' }} data-testid="callback-reason" />
              </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
              <Button onClick={onClose} className="btn-secondary">Cancel</Button>
              <Button onClick={submit} className="btn-primary" disabled={submitting} data-testid="callback-submit">
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <Phone size={14} />}
                <span style={{ marginLeft: 6 }}>Request Callback</span>
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const DocInfoModal = ({ doc, onClose }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
    <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 440, padding: '1.5rem' }} data-testid="doc-info-modal">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.125rem', fontWeight: 700, color: 'var(--navy)', margin: 0 }}>{doc.filename}</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}><X size={20} /></button>
      </div>
      <div style={{ display: 'grid', gap: '0.5rem', fontSize: '0.875rem' }}>
        <InfoRow label="Type" value={doc.doc_type} />
        <InfoRow label="Content" value={doc.content_type || '—'} />
        <InfoRow label="Size" value={doc.size ? `${(doc.size / 1024).toFixed(1)} KB` : '—'} />
        <InfoRow label="Uploaded" value={doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleString() : '—'} />
      </div>
    </div>
  </div>
);

const InfoRow = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem', background: '#F9FAFB', borderRadius: 6 }}>
    <span style={{ color: '#6B7280', fontWeight: 500 }}>{label}</span>
    <span style={{ color: 'var(--navy)', fontWeight: 600 }}>{value}</span>
  </div>
);

export default ClientDashboard;
